import React, { useCallback, useEffect, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  ReactFlowProvider,
  NodeTypes,
  EdgeTypes,
  OnNodesChange,
  OnEdgesChange,
  applyNodeChanges,
  applyEdgeChanges,
  NodeMouseHandler,
  OnNodeDrag,
  MarkerType,
  Node,
  useReactFlow,
  getNodesBounds,
  getViewportForBounds,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { toPng } from 'html-to-image';
import { v4 as uuidv4 } from 'uuid';

import { useDiagramStore } from './store/diagramStore';
import { SERVICE_PALETTE_MIME } from './constants/palette';
import { ServiceData, ServiceGroup } from './types/diagram.types';
import { hasAccess } from 'helpers/access';
import { useProject } from 'contexts/ProjectContext';
import ApiService from 'helpers/apiService';
import ZoneNode from './nodes/ZoneNode';
import ServiceNode from './nodes/ServiceNode';
import ActorNode from './nodes/ActorNode';
import DatabaseNode from './nodes/DatabaseNode';
import KafkaTopicNode from './nodes/KafkaTopicNode';
import KafkaBrokerNode from './nodes/KafkaBrokerNode';
import ConnectorEdge from './edges/ConnectorEdge';
import LeftPanel from './panels/LeftPanel';
import RightPanel from './panels/RightPanel';
import MapToolbar from './toolbar/MapToolbar';
import AddServiceModal from './modals/AddServiceModal';
import EditServiceModal from './modals/EditServiceModal';
import AddActorModal from './modals/AddActorModal';

const nodeTypes: NodeTypes = {
  zone: ZoneNode,
  service: ServiceNode,
  actor: ActorNode,
  database: DatabaseNode,
  kafkaTopic: KafkaTopicNode,
  kafkaBroker: KafkaBrokerNode,
};

const edgeTypes: EdgeTypes = {
  connector: ConnectorEdge,
};

const defaultEdgeOptions = {
  type: 'connector',
  markerEnd: { type: MarkerType.ArrowClosed },
};

const ServiceMapInner: React.FC = () => {
  const {
    nodes,
    edges,
    setNodes,
    setEdges,
    selectNode,
    updateNodePosition,
    getVisibleNodes,
    getHighlightedEdges,
    setProjectServiceFilter,
    setCurrentProjectId,
    addCustomActor,
    addService,
    applyServerLayout,
    getServerLayoutSnapshot,
  } = useDiagramStore();

  const { screenToFlowPosition, getNodes } = useReactFlow();
  const { currentProjectId } = useProject();

  // Per-project positions + service filter
  useEffect(() => {
    // Switch localStorage position key for the new project
    setCurrentProjectId(currentProjectId ?? null);

    if (!currentProjectId) {
      setProjectServiceFilter(null);
      return;
    }
    let cancelled = false;
    ApiService.getServiceConnectByProjectId(currentProjectId)
      .then((sc: unknown) => {
        if (cancelled) return;
        const rec = sc as Record<string, unknown> | null;
        if (!rec) { setProjectServiceFilter(null); return; }
        let configObj: Record<string, unknown> = {};
        let raw = rec.data;
        // Unwrap nested { data: fullRecord }
        if (raw != null && typeof raw === 'object' && 'data' in (raw as object) && !('service_map_ids' in (raw as object))) {
          raw = (raw as Record<string, unknown>).data;
        }
        if (typeof raw === 'string') {
          try { configObj = JSON.parse(raw); } catch { configObj = {}; }
        } else if (raw != null && typeof raw === 'object') {
          configObj = raw as Record<string, unknown>;
        }
        const ids = Array.isArray(configObj.service_map_ids) ? configObj.service_map_ids as string[] : null;
        setProjectServiceFilter(ids && ids.length > 0 ? ids : null);
      })
      .catch(() => {
        if (!cancelled) setProjectServiceFilter(null);
      });
    return () => { cancelled = true; };
  }, [currentProjectId, setProjectServiceFilter, setCurrentProjectId]);

  // OSS-NETVIZ-3: загрузка серверной раскладки (позиции + кастомные сервисы) при смене проекта.
  useEffect(() => {
    if (!currentProjectId) return;
    let cancelled = false;
    ApiService.getServiceMapLayout().then(layout => {
      if (cancelled || !layout) return;
      const custom = Array.isArray(layout.custom_services) ? layout.custom_services : [];
      applyServerLayout(layout.positions || {}, custom);
    });
    return () => { cancelled = true; };
  }, [currentProjectId, applyServerLayout]);

  const [savingLayout, setSavingLayout] = useState(false);
  const saveLayout = useCallback(async () => {
    if (!currentProjectId) return;
    setSavingLayout(true);
    try {
      const snap = getServerLayoutSnapshot();
      await ApiService.saveServiceMapLayout(snap.positions, snap.customServices);
    } catch (e) {
      console.warn('saveServiceMapLayout failed', e);
    } finally {
      setSavingLayout(false);
    }
  }, [currentProjectId, getServerLayoutSnapshot]);

  // OSS-NETVIZ-4: экспорт карты в PNG (html-to-image). Раскрывает внутренние
  // адреса/топологию → гейт serviceConnectList + предупреждение. CSP-safe:
  // узлы — HTML/CSS без cross-origin ассетов.
  const onExportPng = useCallback(() => {
    if (!hasAccess('serviceConnectList')) return;
    if (!window.confirm('Экспорт карты в PNG раскроет внутренние адреса и топологию сервисов. Продолжить?')) return;
    const flowNodes = getNodes();
    if (!flowNodes.length) return;
    const bounds = getNodesBounds(flowNodes);
    const imageWidth = Math.min(Math.max(bounds.width + 200, 800), 4000);
    const imageHeight = Math.min(Math.max(bounds.height + 200, 600), 4000);
    const viewport = getViewportForBounds(bounds, imageWidth, imageHeight, 0.2, 2, 0.1);
    const el = document.querySelector('.react-flow__viewport') as HTMLElement | null;
    if (!el) return;
    toPng(el, {
      backgroundColor: '#F8F9FA',
      width: imageWidth,
      height: imageHeight,
      style: {
        width: `${imageWidth}px`,
        height: `${imageHeight}px`,
        transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
      },
    })
      .then(dataUrl => {
        const a = document.createElement('a');
        a.setAttribute('download', `service-map-${currentProjectId || 'all'}.png`);
        a.setAttribute('href', dataUrl);
        a.click();
      })
      .catch(e => console.warn('export png failed', e));
  }, [getNodes, currentProjectId]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddActorModal, setShowAddActorModal] = useState(false);
  const [editNodeId, setEditNodeId] = useState<string | null>(null);
  const [leftPanelOpen, setLeftPanelOpen] = useState(true);

  const visibleNodes = getVisibleNodes();
  const highlightedEdges = getHighlightedEdges();

  const onNodesChange: OnNodesChange = useCallback(
    changes => {
      setNodes(applyNodeChanges(changes, nodes) as typeof nodes);
    },
    [nodes, setNodes]
  );

  const onEdgesChange: OnEdgesChange = useCallback(
    changes => {
      setEdges(applyEdgeChanges(changes, edges) as any);
    },
    [edges, setEdges]
  );

  const onNodeClick: NodeMouseHandler = useCallback(
    (_event, node) => {
      if (node.type === 'zone') return;
      selectNode(node.id);
    },
    [selectNode]
  );

  const onPaneClick = useCallback(() => {
    selectNode(null);
  }, [selectNode]);

  const onNodeDragStop: OnNodeDrag = useCallback(
    (_event, node) => {
      updateNodePosition(node.id, node.position);
    },
    [updateNodePosition]
  );

  // OSS-NETVIZ-2: drag&drop палитры сервисов на холст. Создаёт локальный узел
  // (коммит в service_connect — OSS-NETVIZ-3). Гейт правом serviceConnectAdd.
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      if (!hasAccess('serviceConnectAdd')) return;
      const group = event.dataTransfer.getData(SERVICE_PALETTE_MIME);
      if (!group) return;
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      const id = `svc-${uuidv4().slice(0, 8)}`;
      const newNode: Node<ServiceData> = {
        id,
        type: 'service',
        position,
        data: {
          label: `Новый сервис`,
          name: 'new-service',
          group: group as ServiceGroup,
          zone: 'zone-internal',
          status: 'planned',
          techStack: [],
          databases: [],
          custom: true, // OSS-NETVIZ-3: drag-added → персистится в service_map_layout
        },
      };
      addService(newNode);
      selectNode(id); // открыть для редактирования в RightPanel
    },
    [screenToFlowPosition, addService, selectNode]
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 56px)',
        overflow: 'hidden',
        background: '#F8F9FA',
      }}
    >
      {/* Top toolbar */}
      <MapToolbar
        onAddService={() => setShowAddModal(true)}
        onSaveLayout={currentProjectId ? saveLayout : undefined}
        savingLayout={savingLayout}
        onExportPng={onExportPng}
      />

      {/* Main area: LeftPanel + Canvas */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left panel */}
        <LeftPanel isOpen={leftPanelOpen} onToggle={() => setLeftPanelOpen(v => !v)} onAddActorClick={() => setShowAddActorModal(true)} />

        {/* React Flow canvas */}
        <div style={{ flex: 1, position: 'relative' }} onDrop={onDrop} onDragOver={onDragOver}>
          <ReactFlow
            nodes={visibleNodes}
            edges={highlightedEdges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            defaultEdgeOptions={defaultEdgeOptions}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            onNodeDragStop={onNodeDragStop}
            fitView
            fitViewOptions={{ padding: 0.05 }}
            minZoom={0.1}
            maxZoom={2.5}
            deleteKeyCode={null}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={20} size={1} color="#E8E8E8" />
            <Controls
              style={{ bottom: 16, right: 16, left: 'auto' }}
              showInteractive={false}
            />
          </ReactFlow>
        </div>
      </div>

      {/* Right panel (offcanvas) */}
      <RightPanel onEdit={id => setEditNodeId(id)} />

      {/* Modals */}
      <AddServiceModal show={showAddModal} onHide={() => setShowAddModal(false)} />
      <EditServiceModal
        show={!!editNodeId}
        nodeId={editNodeId}
        onHide={() => setEditNodeId(null)}
      />
      <AddActorModal
        show={showAddActorModal}
        onHide={() => setShowAddActorModal(false)}
        onAdd={actor => { addCustomActor(actor); setShowAddActorModal(false); }}
      />
    </div>
  );
};

const ServiceMapPage: React.FC = () => (
  <ReactFlowProvider>
    <ServiceMapInner />
  </ReactFlowProvider>
);

export default ServiceMapPage;
